import {
  KeyboardEvent,
  forwardRef,
  Children,
  useLayoutEffect,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { Assert } from '@team/assert';
import { NumberKit } from '@team/number';
import { RootComponent } from '../../../../services/root-component';
import { SlidesProps } from '../../types';
import { Context } from '../../services/context';
import {
  defaultCarouselAriaLabelFormatter,
  defaultSlideAriaLabelFormatter,
} from './model';
import css from './style.module.css';

export const Entry = forwardRef<HTMLDivElement, SlidesProps>(function Component(
  {
    children,
    carouselAriaRoleDescription = 'Карусель',
    carouselAriaLabelFormatter = defaultCarouselAriaLabelFormatter,
    slideAriaRoleDescription = 'Слайд',
    slideAriaLabelFormatter = defaultSlideAriaLabelFormatter,
    ...props
  },
  forwardedRef
) {
  const {
    setSlideCount,
    scrollerRef,
    slideRefsMap,
    createSlideRef,
    currentSlideIndex,
    setCurrentSlideIndex,
    scrollTo,
  } = useContext(Context);

  const slideCount = Children.count(children);

  useLayoutEffect(() => {
    setSlideCount(slideCount);
  }, [slideCount, setSlideCount]);

  /**
   * Индекс слайда, который находится во вьюпорте
   */
  const inViewSlideIndex = useRef<number>(currentSlideIndex);

  useLayoutEffect(() => {
    if (Assert.isNull(scrollerRef.current)) {
      return;
    }

    if (
      Assert.isNull(slideRefsMap.current) ||
      NumberKit.isZero(slideRefsMap.current.size)
    ) {
      return;
    }

    const slides = [...slideRefsMap.current.values()];

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const nextCurrentSlideIndex = slides.indexOf(entry.target);

          inViewSlideIndex.current = nextCurrentSlideIndex;

          setCurrentSlideIndex(nextCurrentSlideIndex);
        }
      },
      {
        root: scrollerRef.current,
        threshold: 0.6,
      }
    );

    slides.forEach((slide) => {
      observer.observe(slide);
    });

    return () => {
      observer.disconnect();
    };
  }, [scrollerRef, slideRefsMap, setCurrentSlideIndex]);

  /**
   * 💬 Логика подскролла к текущему слайду,
   * когда его изменяют из вне.
   */
  useEffect(() => {
    if (currentSlideIndex === inViewSlideIndex.current) {
      return;
    }

    inViewSlideIndex.current = currentSlideIndex;

    scrollTo(currentSlideIndex);
  }, [currentSlideIndex, slideRefsMap, scrollTo]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    let nextCurrentSlideIndex: number;

    if (event.key === 'ArrowLeft') {
      nextCurrentSlideIndex = Math.max(0, currentSlideIndex - 1);
    } else if (event.key === 'ArrowRight') {
      nextCurrentSlideIndex = Math.min(slideCount - 1, currentSlideIndex + 1);
    } else {
      return;
    }

    inViewSlideIndex.current = nextCurrentSlideIndex;

    setCurrentSlideIndex(nextCurrentSlideIndex);
    scrollTo(nextCurrentSlideIndex);
  };

  return (
    <RootComponent
      {...props}
      baseClassName={css.root}
      role="region"
      aria-roledescription={carouselAriaRoleDescription}
      aria-live="polite"
      aria-label={carouselAriaLabelFormatter(currentSlideIndex, slideCount)}
      ref={forwardedRef}
    >
      <div className={css.inner}>
        <div className={css.carousel} ref={scrollerRef}>
          {Children.map(children, (slide, slideIndex) => (
            <div
              key={slideIndex}
              className={css.slide}
              role="group"
              aria-roledescription={slideAriaRoleDescription}
              aria-hidden={slideIndex !== currentSlideIndex}
              aria-label={slideAriaLabelFormatter(slideIndex, slideCount)}
              tabIndex={slideIndex === currentSlideIndex ? 0 : -1}
              ref={createSlideRef(slideIndex)}
              onKeyDown={handleKeyDown}
            >
              {slide}
            </div>
          ))}
        </div>
      </div>
    </RootComponent>
  );
});
